import { Directive, HostListener, Input, NgZone, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ModalService } from '@common/services/modal.service';
import { isMobile } from '@common/utils/isMobile';
import { TranslateService } from '@ngx-translate/core';
import {
    GridComponent,
    GridDataResult,
    PageChangeEvent,
    RowArgs,
    RowClassArgs,
    SelectableSettings,
    SelectAllCheckboxState
} from '@progress/kendo-angular-grid';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { CommonController } from 'app/common/classes/common.controller';
import { CustomExtendedTabService } from 'app/singleton-services/custom-extended-tab.service';
import { fitColumnsToGrid } from './grid-column-layout';
import { applyDateDefaultSort, isDateField } from './sort-utils';

/**
 *  ExtendedBaseTab component which extends CommonController to get access to user, hasPermissions and triggerDeactivationMessage
 *  Also enables selection
 */
@Directive()
export class ExtendedBaseTab extends CommonController {
    protected dialogService: ModalService;
    protected translateService: TranslateService;
    protected ngZone: NgZone;
    constructor(customExtendedTabService: CustomExtendedTabService) {
        super(customExtendedTabService.getDialogService(), customExtendedTabService.getTranslateService());
        this.dialogService = customExtendedTabService.getDialogService();
        this.translateService = customExtendedTabService.getTranslateService();
        this.ngZone = customExtendedTabService.getNgZone();

        if (isMobile()) {
            this.pagingOptions = { type: 'numeric', info: true, buttonCount: 3 };
        }
    }

    attachmentFileUploadRestrictions: FileRestrictions = {
        maxFileSize: 17825792,
        allowedExtensions: [
            '.pdf',
            '.jpg',
            '.png',
            '.csv',
            '.xls',
            '.txt',
            '.xlsx',
            '.doc',
            '.docx',
            '.jpeg',
            '.gif',
            '.bmp',
            '.psd',
            '.xml',
            '.xps',
            '.vsdx',
            '.ttf',
            '.ppt',
            '.pptx'
        ]
    };

    @Input() busyIndicator: boolean;
    @ViewChild(GridComponent) public grid: GridComponent;
    @ViewChildren(GridComponent) grids: QueryList<GridComponent>; // used for detail grids
    // Opt-in per component: spread a detail grid's leftover width across all of its
    // columns instead of piling it on the last one. Use it when the detail grid holds
    // only short columns, where the leftover is big enough to blow the last one up.
    protected distributeDetailGridWidth = false;
    private resizeTimer: ReturnType<typeof setTimeout>;

    public isBusy: boolean = true;
    public itemPage: any;
    public itemsGridView: GridDataResult;
    public multiple = false;
    public allowUnsort = false;
    public pagingOptions: any = { type: 'numeric', info: true, buttonCount: 3, pageSizes: [10, 20, 50] };
    module: string;

    selectAllState: SelectAllCheckboxState = 'unchecked';

    selectableSettings: SelectableSettings = {
        checkboxOnly: false,
        mode: 'multiple'
    };
    selected: any[] = [];

    public sort: SortDescriptor[] = [
        {
            field: 'Id',
            dir: 'desc'
        }
    ];

    protected defaultPaging() {
        this.itemPage = {
            pageSize: 10,
            skip: 0
        };
    }

    protected loadItems(model: any) {
        const ordered = orderBy(model, this.sort);

        this.itemsGridView = {
            data: ordered.slice(this.itemPage.skip, this.itemPage.skip + this.itemPage.pageSize),
            total: model.length
        };
        this.isBusy = false;

        setTimeout(() => {
            if (this.grid && this.grid.columns) {
                this.expandLastColumnToFit();
            }
        });
    }

    pageChange(event: PageChangeEvent, model: any): void {
        this.itemPage.skip = event.skip;
        this.itemPage.pageSize = event.take;
        this.loadItems(model);
    }

    /**
     * Override this method in a subclass to declare additional date fields that should
     * sort DESC by default. Call super.isDateSortField(field) to retain the default logic.
     */
    protected isDateSortField(field: string): boolean {
        return isDateField(field);
    }

    sortChange(sort: SortDescriptor[], model: any): void {
        this.sort = applyDateDefaultSort(sort, this.sort, (f) => this.isDateSortField(f));
        this.loadItems(model);
    }

    protected checkDuplicateItem(item: any, arrayToCheck: any[]) {
        let duplicateExists = false;
        arrayToCheck.forEach((element) => {
            if (element.id == item.id) {
                duplicateExists = true;
            }
        });
        return duplicateExists;
    }

    deletedRow(context: RowClassArgs) {
        if (!context?.dataItem?.active) return 'disabled-row';
    }

    clearSelected() {
        this.selectAllState = 'unchecked';
        this.selected = [];
    }

    onSelectAllChange(checkedState: SelectAllCheckboxState, model: any) {
        if (checkedState === 'checked') {
            this.selected = model.map((item) => item);
            this.selectAllState = 'checked';
        } else {
            this.selected = [];
            this.selectAllState = 'unchecked';
        }
    }

    // angular upgrade fix
    setSelectableSettings(): void {
        this.selectableSettings = {
            checkboxOnly: false,
            mode: 'multiple'
        };
    }

    selectionCallback(context: RowArgs) {
        return context.dataItem.id;
    }

    // check currently selected rows when switching pages
    isRowSelected = (e: RowArgs) => this.selected.indexOf(e.dataItem.id) >= 0;

    // angular upgrade fix
    keyChange(e: any) {
        this.selected = e;
    }

    public onDataStateChange(): void {
        this.expandLastColumnToFit();
    }

    expandDetailGrid() {
        setTimeout(() => {
            const allGrids = this.grids.toArray();
            const latestDetailGrid = allGrids[allGrids.length - 1];
            if (latestDetailGrid?.columnList) {
                this.expandLastColumnToFit(latestDetailGrid, this.distributeDetailGridWidth);
            }
        });
    }

    /**
     * Re-fits the columns to their content and hands the leftover grid width to the last column on screen.
     * The same routine runs after every data load; call it after anything else that changes which
     * columns are shown, their order or the grid width.
     */
    refreshColumnLayout(): void {
        if (this.grid && this.grid.columns) {
            this.expandLastColumnToFit();
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            if (this.grid?.wrapper?.nativeElement.offsetWidth) {
                this.refreshColumnLayout();
            }
        }, 150);
    }

    protected expandLastColumnToFit(grid: GridComponent = this.grid, distributeEvenly: boolean = false) {
        fitColumnsToGrid(grid, distributeEvenly);
    }

    protected processDropdownItems(initialDropdownItems: any[], addedItems: any[], parameter: string = 'id'): any[] {
        {
            if (!initialDropdownItems || !addedItems) return [];

            const dropdownItems = initialDropdownItems.filter((initialDropdownItem) => {
                return !addedItems.some((addedItem) => addedItem[parameter] === initialDropdownItem.id);
            });

            return dropdownItems.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
        }
    }
}
